/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import type { User } from 'firebase/auth';
import { loadFirebaseAuth } from '@/config/firebase-auth';
import { auth_signInGoogle, auth_signInEmail, auth_signUpEmail, auth_logout, auth_signInAnonymously } from '@/lib/firebaseAuth';

interface AuthContextType {
    currentUser: User | null;
    loading: boolean;
    initialized: boolean;
    initializeAuth: () => Promise<void>;
    signInWithGoogle: () => Promise<void>;
    signInWithEmail: (email: string, pass: string) => Promise<void>;
    signUpWithEmail: (email: string, pass: string) => Promise<void>;
    signInAnonymously: () => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const initPromiseRef = useRef<Promise<void> | null>(null);
    const unsubscribeRef = useRef<(() => void) | null>(null);

    const initializeAuth = useCallback(async () => {
        // Prevent concurrent initializations - synchronous ref check
        if (initPromiseRef.current) {
            return initPromiseRef.current;
        }

        if (initialized) {
            return;
        }

        const promise = (async () => {
            try {
                setLoading(true);
                const { auth, firebaseAuth, isConfigured } = await loadFirebaseAuth();

                if (!isConfigured || !auth) {
                    setLoading(false);
                    setInitialized(true);
                    return;
                }

                // Subscribe to auth state changes
                const unsubscribe = firebaseAuth.onAuthStateChanged(auth, (user) => {
                    setCurrentUser(user);
                    setLoading(false);
                });

                // Store unsubscribe for cleanup
                unsubscribeRef.current = unsubscribe;
                setInitialized(true);
            } catch (error) {
                console.error("Auth initialization error:", error);
                setLoading(false);
                setInitialized(true);
            }
        })();

        // Synchronous ref assignment prevents race condition
        initPromiseRef.current = promise;
        return promise;
    }, [initialized]);

    // Auth is initialized on-demand via:
    // 1. handleLogin() when user clicks login button
    // 2. useEffect in AppContent when accessing protected routes (requiresAuth)
    // This avoids blocking the critical path on initial page load

    // Cleanup effect for auth subscription
    useEffect(() => {
        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
            }
        };
    }, []);

    const signInWithGoogle = useCallback(async () => {
        try {
            // Ensure auth listener is active before sign-in so currentUser updates
            if (!initialized) {
                await initializeAuth();
            }
            await auth_signInGoogle();
        } catch (error) {
            console.error(error);
            throw error;
        }
    }, [initialized, initializeAuth]);

    const signInWithEmail = useCallback(async (email: string, pass: string) => {
        try {
            // Ensure auth listener is active before sign-in so currentUser updates
            if (!initialized) {
                await initializeAuth();
            }
            await auth_signInEmail(email, pass);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }, [initialized, initializeAuth]);

    const signUpWithEmail = useCallback(async (email: string, pass: string) => {
        try {
            // Ensure auth listener is active before sign-up so currentUser updates
            if (!initialized) {
                await initializeAuth();
            }
            await auth_signUpEmail(email, pass);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }, [initialized, initializeAuth]);

    const signInAnonymously = useCallback(async () => {
        try {
            if (!initialized) {
                await initializeAuth();
            }
            await auth_signInAnonymously();
        } catch (error) {
            console.error(error);
            throw error;
        }
    }, [initialized, initializeAuth]);

    const logout = useCallback(async () => {
        try {
            await auth_logout();
            // Force reload to clear any local state/cache if needed
            window.location.reload();
        } catch (error) {
            console.error(error);
        }
    }, []);

    const value = useMemo(() => ({
        currentUser,
        loading,
        initialized,
        initializeAuth,
        signInWithGoogle,
        signInWithEmail,
        signUpWithEmail,
        signInAnonymously,
        logout
    }), [currentUser, loading, initialized, initializeAuth, signInWithGoogle, signInWithEmail, signUpWithEmail, signInAnonymously, logout]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
